import { ColDef } from '@ag-grid-community/core'
import { DownloadOutlined, FileExclamationOutlined } from '@ant-design/icons'
import { Alert, Button, Modal, Typography } from 'antd'
import { Spin } from '@surecompanies/core_components'
import React, { useEffect, useState } from 'react'
import { useRestoreWindowScroll } from './holdWindowScroll'
import Grid from 'components/Grid/Grid'
import GridNoResultsOverlay from 'components/GridNoResultsOverlay'
import { useOepDocumentRows } from '../hooks/useOepDocumentRows'
import type { ParsedContributionRow } from '../types'

const { Text } = Typography

interface Props {
  readonly clientId: string
  readonly documentId: string | null
  readonly fileName: string | null
  readonly onClose: () => void
  // Contribution-model download (EMS-10154, popup footer — Figma node
  // 163:4647). Mirrors the table row's own icon: hidden until the
  // analysis_file_uploaded job completes, disabled while no model has been
  // generated yet or a request for this document is already in flight.
  readonly showDownloadContribution: boolean
  readonly isContributionDownloadDisabled: boolean
  readonly isDownloadingContribution: boolean
  readonly onDownloadContribution: () => void
  // Whether the row has an error file (hasErrorFile on the live list row).
  // Drives the popup's error banner (errorMessage from the list is ignored
  // for now — copy is DEFAULT_ERROR_BANNER, or NO_ACCESS_ERROR_BANNER when
  // the error log can't be downloaded, EMS-10969).
  readonly hasErrorFile: boolean
  // Error-log download. Mirrors the table row's FileExclamationOutlined
  // icon: visible only when the row has an error file and the user may
  // download it.
  readonly showDownloadError: boolean
  readonly isDownloadErrorDisabled: boolean
  readonly isDownloadingError: boolean
  readonly onDownloadError: () => void
}

const DEFAULT_ERROR_BANNER =
  'Some rows have conflicting or missing values. Check the error log for details.'

// EMS-10969: same message without pointing at an error log the user can't
// download (no US-restricted data access).
const NO_ACCESS_ERROR_BANNER = 'Some rows have conflicting or missing values.'

// Verbatim from the legacy ContributionTab.tsx grid (naming/shape reuse only
// — see specs/EMS-10154-oep-analysis-upload-history/data-model.md).
const COLUMN_DEFS: ColDef<ParsedContributionRow>[] = [
  { headerName: 'Relationship', field: 'Relationship', pinned: 'left' },
  {
    headerName: 'Contribution Amount',
    field: 'ContributionAmountOrPercentage',
    pinned: 'left',
  },
  { headerName: 'Price Metric', field: 'PriceMetric' },
  { headerName: 'Metal Type', field: 'MetalType' },
  { headerName: 'Min Age', field: 'MinAge' },
  { headerName: 'Max Age', field: 'MaxAge' },
  { headerName: 'State', field: 'State' },
  { headerName: 'RA', field: 'RatingArea' },
  { headerName: 'Employment', field: 'EmploymentStatus' },
  { headerName: 'Exempt', field: 'ExemptStatus' },
  { headerName: 'Contribution Group', field: 'ContributionGroup' },
  { headerName: 'Plan Year', field: 'PlanYear' },
  { headerName: 'Benefit Type', field: 'BenefitType' },
  { headerName: 'Contribution Type', field: 'ContributionType' },
]

export function AnalysisFileDetailModal({
  clientId,
  documentId,
  fileName,
  onClose,
  showDownloadContribution,
  isContributionDownloadDisabled,
  isDownloadingContribution,
  onDownloadContribution,
  hasErrorFile,
  showDownloadError,
  isDownloadErrorDisabled,
  isDownloadingError,
  onDownloadError,
}: Props) {
  const { rows, isLoading, isError } = useOepDocumentRows({
    clientId,
    documentId,
  })

  // Tracks the last documentId whose fetch has actually settled (data or
  // error in hand). Comparing this against the current documentId — rather
  // than trusting isLoading's value on the very first render after a new
  // document opens — guarantees the loader shows immediately every time a
  // document is opened, even on the rare render where react-query hasn't
  // flipped isLoading to true yet (isLoading can briefly read false right
  // after a query goes from disabled/no-cache to enabled, before its fetch
  // has actually started) — that gap used to let AG Grid's empty-rowData
  // default kick in and flash "No Rows to Display" before the real data.
  const [settledDocumentId, setSettledDocumentId] = useState<string | null>(
    null
  )

  useEffect(() => {
    if (documentId === null) {
      // Clear on close so a later reopen of the same documentId (e.g. after
      // its query cache entry has expired) starts unsettled again — otherwise
      // settledDocumentId would still equal documentId and isShowingLoader
      // would stay false through the fresh fetch, reintroducing the flash
      // this state exists to prevent.
      setSettledDocumentId(null)
    } else if (!isLoading) {
      setSettledDocumentId(documentId)
    }
  }, [documentId, isLoading])

  const isShowingLoader =
    documentId !== null && settledDocumentId !== documentId
  const restoreWindowScroll = useRestoreWindowScroll(documentId !== null)

  return (
    <Modal
      open={documentId !== null}
      onCancel={onClose}
      afterOpenChange={restoreWindowScroll}
      title={fileName}
      width={1200}
      centered
      footer={[
        ...(showDownloadError
          ? [
              // `danger` + the table row's own FileExclamationOutlined so this
              // never reads as a second flavour of the neutral "Download
              // contribution" button sitting next to it.
              <Button
                key="download-error"
                danger
                icon={<FileExclamationOutlined />}
                loading={isDownloadingError}
                disabled={isDownloadErrorDisabled}
                onClick={onDownloadError}
              >
                Download Error Log
              </Button>,
            ]
          : []),
        ...(showDownloadContribution
          ? [
              <Button
                key="download-contribution"
                icon={<DownloadOutlined />}
                loading={isDownloadingContribution}
                disabled={isContributionDownloadDisabled}
                onClick={onDownloadContribution}
              >
                Download contribution
              </Button>,
            ]
          : []),
        <Button key="close" onClick={onClose}>
          Close
        </Button>,
      ]}
    >
      {hasErrorFile ? (
        <Alert
          type="error"
          showIcon
          message={
            showDownloadError ? DEFAULT_ERROR_BANNER : NO_ACCESS_ERROR_BANNER
          }
          style={{ marginBottom: 12 }}
        />
      ) : (
        <Text
          type="secondary"
          style={{ fontSize: 12, display: 'block', marginBottom: 12 }}
        >
          Parsed contribution-rule content for this file.
        </Text>
      )}
      {isShowingLoader ? (
        <div
          style={{
            height: 420,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Spin size="large" />
        </div>
      ) : (
        <Grid<ParsedContributionRow>
          wrapper={{ style: { height: 420 } }}
          columnDefs={COLUMN_DEFS}
          rowData={[...rows]}
          defaultColDef={{ sortable: true, resizable: true, filter: false }}
          noRowsOverlayComponent={GridNoResultsOverlay}
        />
      )}
      {isError && (
        <Text
          type="danger"
          style={{ fontSize: 12, marginTop: 8, display: 'block' }}
        >
          Could not load this file's parsed content. Please try again.
        </Text>
      )}
    </Modal>
  )
}
